import { useToast } from '@chakra-ui/react';
import { ActionPopoverContent } from '@gamma/ui/overlay';
import { useContext } from 'react';
import { useSearchParams } from 'react-router';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers/AuthProvider';
import { useListUsers } from '../../../../../admin/queries';
import { DetectionQueryVariables } from '../../../../pages';
import {
  GET_DETECTIONS,
  QUERY_DETECTIONS_PAGINATED,
  useAssignDetections,
} from '../../../../queries';

import { IGetDetectionVariables } from '../../../DetectionDrawer';
import { useDetectionContext } from '@investigator/features/detections/context';
import { filterAssignableUsers } from '@investigator/shared/utils';
import { Detection } from '@investigator/shared/generated/graphql';

const { controls, toasts } = i18n.pages.detections;
const { assignUser } = controls;

interface AssignUserContentProps {
  onClose?: () => void;
  refetchMain?: boolean;
  setUpdateDetectionsParam?: () => void;
  getDetectionVariables?: IGetDetectionVariables;
  queryDetectionsPaginatedVariables?: DetectionQueryVariables;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading?: boolean;
  detection?: Detection; // Optional detection prop to override context
}

export const AssignUserContent = ({
  onClose,
  isLoading,
  setUpdateDetectionsParam,
  setIsLoading,
  getDetectionVariables,
  queryDetectionsPaginatedVariables,
  refetchMain,
  detection: detectionProp,
}: AssignUserContentProps) => {
  const [_, setSearchParams] = useSearchParams();
  const showToastMessage = useToast();
  const { user } = useContext(AuthContext);
  const { detectionData: detectionDataFromContext } = useDetectionContext();

  // Use the prop detection if provided (for row actions), otherwise fall back to context
  const detectionData = detectionProp ?? detectionDataFromContext;
  const { assignment_info, detection_id, update_info } = detectionData || {};

  const { loading: listUsersLoading, data: { listUsers = [] } = {} } =
    useListUsers({
      skip: !user?.attributes?.['custom:tenant_id'],
      variables: {
        tenants: [`${user?.attributes?.['custom:tenant_id']}`],
        groups: ['admin', 'analyst'],
      },
    });

  const refetchQueries = [
    {
      query: GET_DETECTIONS,
      variables: getDetectionVariables,
    },
  ];

  const [assignDetections, { loading: assignLoading }] = useAssignDetections({
    awaitRefetchQueries: true,
    refetchQueries: !queryDetectionsPaginatedVariables
      ? refetchQueries
      : [
          ...refetchQueries,
          {
            query: QUERY_DETECTIONS_PAGINATED,
            variables: queryDetectionsPaginatedVariables,
          },
        ],
    onCompleted: () => {
      setIsLoading && setIsLoading(false);
      setUpdateDetectionsParam?.();
      showToastMessage({
        status: 'success',
        title: toasts.success,
        description: toasts.assigneeSet,
        isClosable: true,
        position: 'bottom-right',
      });
      onClose && onClose();
    },
    onError: () => {
      setIsLoading && setIsLoading(false);
      showToastMessage({
        status: 'error',
        title: toasts.error,
        description: toasts.assigneeNotSet,
        isClosable: true,
        position: 'bottom-right',
      });
    },
  });

  const assignableUsers = filterAssignableUsers({ user, users: listUsers });

  return (
    <ActionPopoverContent
      withArrow={false}
      title={assignUser}
      onCancel={onClose}
      isSearchable={true}
      data-testid="assign-detection-popover-content"
      value={assignment_info?.assigned_to_username ?? undefined}
      isLoading={assignLoading || isLoading || listUsersLoading}
      options={assignableUsers}
      onApply={(selectedValue) => {
        setIsLoading && setIsLoading(true);
        assignDetections({
          variables: {
            items: [
              {
                assignment_info: {
                  assigned_to_username: selectedValue || null,
                },
                update_info: {
                  last_updated_timestamp: update_info?.last_updated_timestamp,
                },
                detection_id,
                tenant: user?.attributes?.['custom:tenant_id'],
              },
            ],
          },
        });
        if (refetchMain) {
          setSearchParams((params) => {
            params.set('refetch_main', 'true');
            return params;
          });
        }
      }}
    />
  );
};
